import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import RefreshIcon from '@mui/icons-material/Refresh'
import { auditDayOf, auditDayRange, DocumentJobOutcome, DocumentJobProducer, DocumentJobStatus, DocumentPipelineAuditResponse } from '@numeo/types-core'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiErrorMessage, PIPELINE_PRODUCER_LABELS, pipelineAuditApi } from './api'
import { dayTotals, formatAuditDay, formatDuration, percent } from './format'
import CountTable from './components/CountTable'
import DailyTable from './components/DailyTable'
import StatCard from './components/StatCard'
import InfoHint from './components/InfoHint'
import Tooltip from '@mui/joy/Tooltip'
import { HINTS, STATUS_HINTS } from './hints'
import { timeZoneName, VIEWER_TIME_ZONE } from './viewerTimeZone'
import { FAILED_STATUS_FILTER } from '../document-jobs/statusFilter'

/** How many days the day list reaches back. */
const LIST_DAYS = 14

/**
 * How the document pipeline (ADR-022) is doing, one day at a time in the viewer's own time zone: click a day and every number on the page
 * (volume and outcomes, text vs visual extraction, latency, how the shadow run agrees with the legacy ratecon chain,
 * the document and OCR mix, why jobs failed) is that day's. What is queued right now is live and stays separate.
 */
export default function PipelineAuditPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const today = auditDayOf(new Date(), VIEWER_TIME_ZONE)
    const dayParam = searchParams.get('day')
    // A malformed or future day in the URL falls back to today; the server rejects what the page would never send.
    const selectedDay = dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam) && dayParam <= today ? dayParam : today
    const selectDay = (day: string) => {
        const next = new URLSearchParams(searchParams)
        if (day === today) next.delete('day')
        else next.set('day', day)
        setSearchParams(next, { replace: true })
    }
    const [producer, setProducer] = React.useState<DocumentJobProducer | null>(DocumentJobProducer.RATECON_EMAIL_SHADOW)
    const [projectName, setProjectName] = React.useState(searchParams.get('projectName') ?? '')
    const [data, setData] = React.useState<DocumentPipelineAuditResponse | null>(null)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    // Only the newest request may write its answer: clicking through days quickly must not let a slow earlier one win.
    const latestRequest = React.useRef(0)

    const load = React.useCallback(async () => {
        const request = ++latestRequest.current
        setLoading(true)
        setError(null)
        try {
            const audit = await pipelineAuditApi.getAudit({
                days: LIST_DAYS,
                day: selectedDay,
                timeZone: VIEWER_TIME_ZONE,
                producer: producer ?? undefined,
                projectName: projectName.trim() || undefined,
            })
            if (request === latestRequest.current) setData(audit)
        } catch (err) {
            if (request === latestRequest.current) setError(apiErrorMessage(err, 'Failed to load the pipeline audit'))
        } finally {
            if (request === latestRequest.current) setLoading(false)
        }
    }, [selectedDay, producer, projectName])

    React.useEffect(() => {
        void load()
        // Reload when the day or producer changes; the tenant field reloads on Enter or Refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, producer])

    // The numbers of the selected day, from the day list; the failures come back for that day alone.
    const dayRow = data?.days.find((d) => d.day === selectedDay)
    const totals = data ? dayTotals(dayRow) : null
    const dataIsForSelectedDay = data?.day === selectedDay

    /** Open Document Jobs with this page's producer and tenant, for the selected day (midnight to midnight in the viewer's time zone), plus the card's own filter. */
    const drill = (filter: { status?: string; outcome?: DocumentJobOutcome }) => () => {
        const { start, end } = auditDayRange(selectedDay, VIEWER_TIME_ZONE)
        const params = new URLSearchParams({ startDate: start.toISOString(), endDate: new Date(end.getTime() - 1).toISOString() })
        if (producer) params.set('producer', producer)
        if (projectName.trim()) params.set('projectName', projectName.trim())
        if (filter.status) params.set('status', filter.status)
        if (filter.outcome) params.set('outcome', filter.outcome)
        navigate(`/document-pipeline/document-jobs?${params.toString()}`)
    }
    /**
     * The jobs behind "Shadow fields differ", one by one, in the audit's own flow. The view (day and tenant) is written to this page's
     * address first, so Back from the list returns to exactly this.
     */
    const openShadowDifferences = () => {
        const params = new URLSearchParams()
        if (selectedDay !== today) params.set('day', selectedDay)
        if (projectName.trim()) params.set('projectName', projectName.trim())
        setSearchParams(params, { replace: true })
        const query = params.toString()
        navigate(`/document-pipeline/audit/shadow-differences${query ? `?${query}` : ''}`)
    }
    const finished = totals ? totals.done + totals.notOfInterest + totals.combined + totals.failed : 0
    const oldest = data?.backlog.oldestInFlightAt ? Math.round((Date.now() - new Date(data.backlog.oldestInFlightAt).getTime()) / 1000) : null

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, md: 2 } }}>
            <Box>
                <Typography level="h2">Document Pipeline Audit</Typography>
                <Typography level="body-sm">
                    Every document the pipeline took in, by the day it was enqueued ({timeZoneName()}, your time zone). Click a day to see all of its numbers.
                    Shadow jobs run alongside the legacy ratecon chain and are compared with what it saved.
                </Typography>
            </Box>

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl size="sm">
                    <FormLabel>
                        <InfoHint hint={HINTS.producer}>Producer</InfoHint>
                    </FormLabel>
                    <Select size="sm" value={producer} onChange={(_, v) => setProducer(v)} sx={{ minWidth: 220 }} placeholder="All producers">
                        <Option value={null}>All producers</Option>
                        {Object.entries(PIPELINE_PRODUCER_LABELS).map(([value, label]) => (
                            <Option key={value} value={value}>
                                {label}
                            </Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>
                        <InfoHint hint={HINTS.tenant}>Tenant (projectName)</InfoHint>
                    </FormLabel>
                    <Input
                        size="sm"
                        value={projectName}
                        placeholder="All tenants"
                        onChange={(e) => setProjectName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && load()}
                    />
                </FormControl>
                <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={loading ? <CircularProgress size="sm" /> : <RefreshIcon />}
                    onClick={load}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Sheet>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}

            {data && totals && (
                <>
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'md', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography level="title-sm" sx={{ mr: 1 }}>
                            <InfoHint hint={HINTS.backlog}>In the pipeline now (all producers)</InfoHint>
                        </Typography>
                        {Object.keys(data.backlog.byStatus).length === 0 ? (
                            <Chip size="sm" color="success" variant="soft">
                                empty
                            </Chip>
                        ) : (
                            Object.entries(data.backlog.byStatus).map(([status, n]) => (
                                <Tooltip key={status} arrow title={STATUS_HINTS[status] ?? status} variant="outlined" sx={{ maxWidth: 320 }}>
                                    <Chip size="sm" variant="outlined">
                                        {status}: {n}
                                    </Chip>
                                </Tooltip>
                            ))
                        )}
                        <Tooltip arrow title={HINTS.runningBatches} variant="outlined" sx={{ maxWidth: 320 }}>
                            <Chip size="sm" variant="outlined">
                                running batches: {data.backlog.runningBatches}
                            </Chip>
                        </Tooltip>
                        {oldest !== null && (
                            <Tooltip arrow title={HINTS.oldestWaiting} variant="outlined" sx={{ maxWidth: 320 }}>
                                <Chip size="sm" variant="soft" color={oldest > 6 * 3600 ? 'warning' : 'neutral'}>
                                    oldest waiting {formatDuration(oldest)}
                                </Chip>
                            </Tooltip>
                        )}
                    </Sheet>

                    <DailyTable days={data.days} selectedDay={selectedDay} onSelect={selectDay} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', opacity: dataIsForSelectedDay && !loading ? 1 : 0.55 }}>
                        <Typography level="title-lg">{formatAuditDay(selectedDay)}</Typography>
                        <Chip size="sm" variant="soft">
                            {timeZoneName()}
                        </Chip>
                        {selectedDay === today && (
                            <Chip size="sm" variant="soft" color="primary">
                                today
                            </Chip>
                        )}
                        {loading && <CircularProgress size="sm" />}
                    </Box>

                    {dayRow ? (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                            <StatCard
                                info={HINTS.enqueued}
                                label="Enqueued"
                                onClick={drill({})}
                                value={totals.enqueued}
                                hint={`${totals.deduplicated} waited on an identical document`}
                            />
                            <StatCard
                                info={HINTS.done}
                                label="Done"
                                onClick={drill({ status: DocumentJobStatus.LLM_DONE })}
                                value={totals.done}
                                tone="success"
                                hint={percent(totals.done, finished) + ' of finished'}
                            />
                            <StatCard
                                info={HINTS.notOfInterest}
                                label="Not of interest"
                                onClick={drill({ status: DocumentJobStatus.LLM_NOT_OF_INTEREST })}
                                value={totals.notOfInterest}
                                hint={percent(totals.notOfInterest, finished) + ' of finished'}
                            />
                            <StatCard
                                info={HINTS.combined}
                                label="Several documents"
                                onClick={drill({ status: DocumentJobStatus.LLM_COMBINED })}
                                value={totals.combined}
                                tone={totals.combined ? 'warning' : 'neutral'}
                                hint={percent(totals.combined, finished) + ' of finished'}
                            />
                            <StatCard
                                label="Failed"
                                onClick={drill({ status: FAILED_STATUS_FILTER })}
                                info={HINTS.failed}
                                value={totals.failed}
                                tone={totals.failed ? 'danger' : 'neutral'}
                                hint={percent(totals.failed, finished) + ' of finished'}
                            />
                            <StatCard
                                info={HINTS.textVisual}
                                label="Text / visual"
                                value={`${totals.textRoute} / ${totals.visualRoute}`}
                                hint="extracted from OCR text vs the document"
                            />
                            <StatCard
                                label="Shadow agreed"
                                onClick={drill({ outcome: DocumentJobOutcome.SHADOW_AGREED })}
                                info={HINTS.shadowAgreed}
                                value={percent(totals.shadowAgreed, totals.shadowCompared)}
                                tone={totals.shadowCompared && totals.shadowAgreed / totals.shadowCompared < 0.9 ? 'warning' : 'success'}
                                hint={
                                    totals.shadowCompared
                                        ? `${totals.shadowAgreed} of ${totals.shadowCompared} compared · avg score ${(totals.shadowScoreSum / totals.shadowCompared).toFixed(3)}`
                                        : 'nothing compared yet'
                                }
                            />
                            <StatCard
                                label="Shadow fields differ"
                                info={HINTS.shadowMismatched}
                                onClick={openShadowDifferences}
                                linkTitle="See the jobs that differ, field by field, next to the document"
                                value={totals.shadowCompared - totals.shadowAgreed}
                                tone={totals.shadowCompared - totals.shadowAgreed ? 'warning' : 'neutral'}
                                hint="compared with legacy, at least one field differs · click to review"
                            />
                            <StatCard
                                label="Shadow disagreements"
                                onClick={drill({ outcome: DocumentJobOutcome.SHADOW_LEGACY_NONE })}
                                info={HINTS.shadowDisagreements}
                                value={totals.shadowLegacyNone}
                                tone={totals.shadowLegacyNone ? 'warning' : 'neutral'}
                                hint={`pipeline found a ratecon, legacy didn't · ${totals.shadowLegacyPending} legacy still pending · ${totals.shadowAgreeNone} neither`}
                            />
                            <StatCard
                                label="Result handler gave up"
                                onClick={drill({ outcome: DocumentJobOutcome.HANDLER_GAVE_UP })}
                                info={HINTS.handlerGaveUp}
                                value={totals.resultHandlerFailed}
                                tone={totals.resultHandlerFailed ? 'warning' : 'neutral'}
                                hint="domain record not created after retries"
                            />
                            <StatCard
                                label="LLM tokens"
                                info={HINTS.llmTokens}
                                value={`${Math.round((totals.llmInputTokens + totals.llmOutputTokens) / 1000).toLocaleString()}k`}
                                hint={`${Math.round(totals.llmInputTokens / 1000).toLocaleString()}k input · ${Math.round(totals.llmOutputTokens / 1000).toLocaleString()}k output`}
                            />
                        </Box>
                    ) : (
                        <Alert color="neutral" variant="soft">
                            No documents were enqueued on this day with these filters.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <CountTable
                            title="Document types (finished)"
                            info={HINTS.docTypes}
                            rows={Object.entries(totals.byDocType).map(([label, count]) => ({ label, count }))}
                            empty="No finished documents."
                        />
                        <CountTable
                            title="OCR adapter behind the text"
                            info={HINTS.ocrAdapters}
                            rows={Object.entries(totals.byOcrAdapter).map(([label, count]) => ({ label, count }))}
                            empty="No OCR text yet."
                        />
                        <CountTable
                            title="Top failure reasons"
                            info={HINTS.topFailures}
                            rows={data.topFailures.map((f) => ({ label: f.reason, count: f.count }))}
                            empty="No failures on this day."
                        />
                        <CountTable
                            title="Failed OCR tries by reader (window)"
                            info={HINTS.ocrFailures}
                            rows={Object.entries(
                                data.ocrFailures.reduce<Record<string, number>>((acc, f) => {
                                    const label = `${f.reader} · ${f.kind}`
                                    acc[label] = (acc[label] ?? 0) + f.count
                                    return acc
                                }, {})
                            ).map(([label, count]) => ({ label, count }))}
                            empty="No failed OCR tries in the window."
                        />
                    </Box>
                </>
            )}
        </Box>
    )
}
